// Helpers for reading Gerber text alongside gerber-to-svg's render. Pure
// functions (no DOM) so they can be unit-tested directly.

export type Box = {x: number; y: number; w: number; h: number};

// gerber-to-svg's user space is file units × 1000, so dividing a user coordinate
// by 1000 gives the file's own unit (mm or inch); %MO says which to label it.
// Allegro writes %MO inside the %FS block (%FSLAX55Y55*MOIN*%).
export function gerberUnit(text: string): 'mm' | 'in' {
  return /[%*]MOIN\*/.test(text) ? 'in' : 'mm';
}

// gerber-to-svg reports no error for text that isn't Gerber; it renders an
// empty viewBox. A valid layer with no artwork also renders empty, but it has a
// %FS format block, which other text does not.
export function isNonGerberRender(viewBox: number[], hasFormat: boolean): boolean {
  const isEmpty = !(viewBox[2] > 0 && viewBox[3] > 0);
  return isEmpty && !hasFormat;
}

// gerber-to-svg ignores %IPNEG*% (image polarity negative) and draws the layer
// as positive, so a negative plane would show its clearances as copper. Allegro
// often writes IPNEG inside a combined %IR0*IPNEG*...% block.
export function isNegativeImage(text: string): boolean {
  for (const block of extendedBlocks(text)) {
    if (block.split('*').some((command) => command.trim() === 'IPNEG')) return true;
  }
  return false;
}

// Yield the body of each %...% extended block. Outside a block, everything up to
// the next '*' is one data block, so a '%' inside a G04 comment (G04 100% fill*)
// does not open a block.
function* extendedBlocks(text: string): Generator<string> {
  let i = 0;
  while (i < text.length) {
    if (text[i] === '%') {
      const end = text.indexOf('%', i + 1);
      if (end === -1) return;
      yield text.slice(i + 1, end);
      i = end + 1;
    } else if (/\s/.test(text[i])) {
      i++;
    } else {
      const end = text.indexOf('*', i);
      if (end === -1) return;
      i = end + 1;
    }
  }
}
